import React, { useState, useEffect } from "react";
import { Tooltip, Button, Tag, Typography } from "antd";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  reload,
  loadPage,
  changePage,
  setMonth,
  setYear,
  setBranch,
  setTextFilter,
  resetSalary,
  approveEmployee,
  unapproveEmployee,
  recalculateEmployee,
} from "./salarySlice";
import { fetchList } from "../../api/server";
import { updateRunTimeFilters, loadRecord } from "../report/reportSlice";
import Report from "../report";
import ReportModal from "../../components/templates/ReportModal";
import { useIntl } from "react-intl";
import _ from "lodash";
import ActionsTable from "../../components/templates/ActionsTable";
import Surface from "../../components/layouts/Surface";
import Age from "../../components/tables/Age";
import Gender from "../../components/tables/Gender";
import clientsImg from "../../assets/no-clients.svg";
import Header from "./components/Header";
import AvatarComponent from "./tableItems/AvatarComponent";
import FullName from "./tableItems/FullName";
import Hours from "./tableItems/Hours";
import EmployeeTypes from "../../components/tables/EmployeeTypes";
import EmployeeSalaryModal from "./employeeSalary/index";
import { useLocation, useParams } from "react-router-dom";
import { useSalary_column_settings } from "../../hooks";
import {
  CalculatorOutlined,
  CheckSquareOutlined,
  EditOutlined,
  UnlockOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  RootState,
  SalaryColumnSetting,
  SalaryMode,
  EmployeeRecord,
} from "./types";

const { Text } = Typography;

interface BranchParams {
  branchId?: string;
}

/**
 * Salary Management Component
 *
 * A comprehensive interface for managing employee salary data with the following features:
 *
 * @component
 *
 * Main features:
 * - Displays a table of employee salary information with filterable and sortable columns
 * - Supports viewing and editing of individual employee salary details
 * - Provides salary approval workflow functionality
 * - Supports recalculation of employee salaries
 * - Allows filtering by various employee attributes (gender, ID, approval status, etc.)
 * - Shows summary information about approval status and totals
 * - Integrates with reporting system for salary exports
 *
 * User interactions:
 * - Filter employees by text search, branch, date, and employee attributes
 * - View or edit detailed salary information for individual employees
 * - Approve or unapprove employee salaries for payment processing
 * - Recalculate salaries when timesheet data changes
 * - Export salary data to reports
 *
 * Data management:
 * - Loads and displays employee salary data for selected month/year/branch
 * - Tracks approval status of each employee's salary
 * - Supports pagination for handling large datasets
 * - Maintains view state (filters, sort order, etc.) during user interaction
 *
 * Display modes:
 * - View mode: When salaries are approved or locked
 * - Edit mode: When salaries are open for modification
 *
 * @returns {JSX.Element} Salary management interface
 */
const Salary = (): JSX.Element => {
  const dispatch = useDispatch<any>();
  const intl = useIntl();
  const location = useLocation();
  const { branchId } = useParams<BranchParams>();
  const salaryColumnSettings =
    useSalary_column_settings() as SalaryColumnSetting[];

  // State
  const [mode, setMode] = useState<SalaryMode>(null);
  const [activeDate, setActiveDate] = useState<moment.Moment>(
    moment().subtract(1, "months")
  );
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [isLocked, setIsLocked] = useState<boolean | null>(null);
  const [employeeSalaryVisible, setEmployeeSalaryVisible] =
    useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeRecord | null>(null);
  const [reportVisible, setReportVisible] = useState<boolean>(false);

  // Selectors
  const activeYear = useSelector((state: RootState) => state.salary.activeYear);
  const activeMonth = useSelector(
    (state: RootState) => state.salary.activeMonth
  );
  const activeBranch = useSelector(
    (state: RootState) => state.salary.activeBranch
  );
  const activeClient = useSelector(
    (state: RootState) => state.salary.activeClient
  );
  const scopePrefix = useSelector((state: RootState) => state.user.scopePrefix);
  const { page: salaryPage, customSort } = useSelector(
    (state: any) => state.salary
  );
  const { current, pageSize, total, items, order, filter, summary } =
    salaryPage;

  const loading = useSelector(
    (state: RootState) => state.pendingActions["salary/loadPage"]
  );
  const salaryExportTypeId = useSelector(
    (state: RootState) => state.organization.details.salary_export_type_id
  );
  const activeReport = useSelector(
    (state: RootState) => state.report.activeReport
  );
  const isAdmin = location.pathname.includes("admin");

  // Effects
  useEffect(() => {
    setMode(isApproved || isLocked ? "view" : "edit");
  }, [isApproved, isLocked]);

  useEffect(() => {
    setIsLocked(summary?.locked ?? null);
  }, [summary]);

  useEffect(() => {
    dispatch(loadPage(isAdmin));
  }, [
    dispatch,
    isAdmin,
    activeMonth,
    activeYear,
    activeBranch,
    activeClient,
    filter,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetSalary(null));
    };
  }, [dispatch]);

  /**
   * Handles text search filter changes
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event from the search input
   */
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch(setTextFilter({ text: e.target.value }));

  /**
   * Reloads salary data with current filters
   */
  const reloadSalary = () => dispatch(loadPage(isAdmin));

  /**
   * Toggles the employee salary modal and sets view/edit mode
   *
   * @param {EmployeeRecord | null} record - The employee record to view/edit or null to close
   * @param {boolean} viewMode - Whether to open in view-only mode
   */
  const toggleEmployeeSalaryModal = (
    record: EmployeeRecord | null,
    viewMode?: boolean
  ) => {
    setSelectedEmployee(record);
    setMode(viewMode ? "view" : "edit");
    setEmployeeSalaryVisible((prev) => !prev);
  };

  /**
   * Updates active month and year based on date selection
   *
   * @param {moment.Moment | null} date - The selected date
   */
  const handleDateChange = (date: moment.Moment | null) => {
    const month = date?.format("M") ?? null;
    const year = date?.format("YYYY") ?? null;
    dispatch(setMonth({ month }));
    dispatch(setYear({ year }));
    setActiveDate(date as moment.Moment);
  };

  /**
   * Updates the active branch filter
   *
   * @param {string} val - Selected branch ID
   */
  const handleBranchChange = (val: string) =>
    dispatch(setBranch({ branch: val }));

  /**
   * Triggers recalculation of employee salary data
   *
   * @param {number} [employeeId] - Optional employee ID to recalculate single employee
   * @returns {Promise<void>} Promise resolving after recalculation completes
   */
  const handleRecalculate = async (employeeId?: number) => {
    const params = {
      branchId: activeBranch || branchId,
      employeeId,
      period: { year: +activeYear, month: +activeMonth },
    };
    const action = await dispatch(recalculateEmployee(params));
    if (!action.error) dispatch(loadPage(isAdmin));
  };

  /**
   * Handles approving or unapproving employee salaries
   *
   * @param {boolean} currentApprovalStatus - Current approval status
   * @param {number} [employeeId] - Optional employee ID for single employee approval
   * @returns {Promise<void>} Promise resolving after approval status change
   */
  const handleApprove = async (
    currentApprovalStatus: boolean,
    employeeId?: number
  ) => {
    const params = {
      branchId: activeBranch || branchId,
      employeeId,
      period: { year: +activeYear, month: +activeMonth },
    };
    const action = await dispatch(
      currentApprovalStatus
        ? unapproveEmployee(params)
        : approveEmployee(params)
    );
    if (!action.error) {
      setIsApproved(!currentApprovalStatus);
      if (selectedEmployee) {
        setSelectedEmployee({
          ...selectedEmployee,
          approved: !currentApprovalStatus,
        });
      }
      dispatch(loadPage(isAdmin));
    }
  };

  /**
   * Opens the salary report dialog
   * Fetches the appropriate report and applies branch filters
   *
   * @returns {Promise<void>} Promise resolving after report is loaded
   */
  const handleShowReport = async () => {
    const params = {
      current: 1,
      pageSize: 1,
      filter: { freeText: "שעות מאושרות לשכר ב.לאומי ואחרים" },
    };
    const { error, data } = await fetchList("reports", params);
    if (!error && data?.items?.[0]) {
      const branch = activeBranch || branchId;
      dispatch(
        updateRunTimeFilters([
          {
            member: "Branches.name_by_id",
            operator: "equals",
            values: [branch?.toString() || ""],
          },
        ])
      );
      dispatch(loadRecord({ scopePrefix, id: data.items[0].id }));
      setReportVisible(true);
    }
  };

  /**
   * Builds the table column configuration
   *
   * @returns {Array} Column definitions for the employee salary table
   */
  const getTableColumns = () => {
    const firstItems = [
      {
        title: "avatar_url",
        render: AvatarComponent,
        width: "60px",
        fixed: "left",
      },
      {
        title: "employeeName",
        render: FullName,
        sortIndex: "surename",
        sorter: true,
        width: "150px",
        fixed: "left",
      },
      {
        title: "accounting_number",
        width: "120px",
        sortIndex: "accounting_number",
        sorter: true,
      },
      {
        title: "employee_type_id",
        render: ({ record }: { record: EmployeeRecord }) => (
          <EmployeeTypes text={record?.employee_type_id} />
        ),
        width: "110px",
        filters: [
          {
            text: (
              <FormattedMessage id="app.dataSelect.employee_type_id.israel_citizen" />
            ),
            value: 1,
          },
          {
            text: (
              <FormattedMessage id="app.dataSelect.employee_type_id.foreign_citizen" />
            ),
            value: 2,
          },
          {
            text: (
              <FormattedMessage id="app.dataSelect.employee_type_id.territories_citizen" />
            ),
            value: 3,
          },
        ],
      },
      {
        title: "idOrPassport",
        render: ({ record }: { record: EmployeeRecord }) => (
          <Text>
            {record?.employee_type_id === 1
              ? record.local_id
              : record.foreign_passport_number}
          </Text>
        ),
        width: "120px",
      },
      {
        title: "gender_id",
        responsive: ["xl"],
        render: Gender,
        width: "70px",
        filters: [
          {
            text: <FormattedMessage id="app.dataSelect.gender_id.male" />,
            value: 1,
          },
          {
            text: <FormattedMessage id="app.dataSelect.gender_id.female" />,
            value: 2,
          },
        ],
      },
      {
        title: "age",
        responsive: ["xl"],
        render: Age,
        columnProps: { field: "birthday" },
        width: "60px",
        sortIndex: "birthday",
        sorter: true,
      },
      {
        title: "transportation_cost",
        width: "125px",
        ellipsis: true,
        sortIndex: "transportation_cost",
        sorter: true,
      },
      {
        title: "scheduled_minutes",
        sortIndex: "scheduled_minutes",
        sorter: true,
        render: Hours,
        width: "140px",
        ellipsis: true,
      },
      {
        title: "approved_minutes",
        sortIndex: "approved_minutes",
        sorter: true,
        render: Hours,
        width: "140px",
        ellipsis: true,
      },
      {
        title: "retro_minutes",
        sortIndex: "retro_minutes",
        sorter: true,
        render: Hours,
        width: "140px",
        ellipsis: true,
      },
      {
        title: "total_working_days",
        sortIndex: "total_working_days",
        sorter: true,
        width: "120px",
        ellipsis: true,
      },
      {
        title: "holiday_minutes",
        sortIndex: "holiday_minutes",
        sorter: true,
        render: Hours,
        width: "120px",
        ellipsis: true,
      },
      {
        title: "regular_minutes",
        render: Hours,
        sortIndex: "regular_minutes",
        sorter: true,
        width: "125px",
        ellipsis: true,
      },
      {
        title: "extra_minutes_rate1",
        render: Hours,
        sortIndex: "extra_minutes_rate1",
        sorter: true,
        width: "135px",
        ellipsis: true,
      },
      {
        title: "extra_minutes_rate2",
        render: Hours,
        sortIndex: "extra_minutes_rate2",
        sorter: true,
        width: "135px",
        ellipsis: true,
      },
      {
        title: "extra_minutes_rate3",
        render: Hours,
        sortIndex: "extra_minutes_rate3",
        sorter: true,
        width: "135px",
        ellipsis: true,
      },
      {
        title: "extra_minutes_rate4",
        render: Hours,
        sortIndex: "extra_minutes_rate4",
        sorter: true,
        width: "135px",
        ellipsis: true,
      },
    ];

    const lastItems = [
      {
        title: "exported_to_finance",
        width: "90px",
        sortIndex: "exported_to_finance",
        render: ({ record }: { record: EmployeeRecord }) => (
          <Text>
            {record.exported_to_finance
              ? intl.formatMessage({ id: "app.forms.yes" })
              : intl.formatMessage({ id: "app.forms.no" })}
          </Text>
        ),
        filters: [
          { text: <FormattedMessage id="app.forms.yes" />, value: true },
          { text: <FormattedMessage id="app.forms.no" />, value: false },
        ],
      },
      {
        render: ({ record }: { record: EmployeeRecord }) =>
          record.approved ? (
            <Tag>
              <FormattedMessage id="app.salary.approved_status" />
            </Tag>
          ) : null,
        title: "approved",
        width: "80px",
        fixed: "right",
        filters: [
          {
            text: <FormattedMessage id="app.salary.approved_for_salary" />,
            value: true,
          },
          {
            text: <FormattedMessage id="app.salary.unapproved_for_salary" />,
            value: false,
          },
        ],
      },
      {
        title: "actions",
        responsive: ["md"],
        width: "100px",
        fixed: "right",
        render: ({ record }: { record: EmployeeRecord }) => (
          <div style={{ display: "flex", gap: 8 }}>
            <Tooltip
              title={intl.formatMessage({
                id:
                  record.approved || summary?.locked
                    ? "app.salary.view_employee_salary"
                    : "app.salary.edit_employee_salary",
              })}
            >
              <Button
                type="text"
                icon={
                  record.approved || summary?.locked ? (
                    <SearchOutlined />
                  ) : (
                    <EditOutlined />
                  )
                }
                onClick={() =>
                  toggleEmployeeSalaryModal(
                    record,
                    record.approved || summary?.locked
                  )
                }
              />
            </Tooltip>
            {!(record.approved || summary?.locked) && (
              <Tooltip
                title={intl.formatMessage({ id: "app.salary.recalculate" })}
              >
                <Button
                  type="text"
                  icon={<CalculatorOutlined />}
                  onClick={() => handleRecalculate(record.employee_id)}
                />
              </Tooltip>
            )}
            {summary?.locked || !record.salary_run_at ? null : (
              <Tooltip
                title={intl.formatMessage({
                  id: record.approved
                    ? "app.salary.unlock_employee_salary"
                    : "app.salary.approve_employee_salary",
                })}
              >
                <Button
                  type="text"
                  style={{ color: record.approved ? "#faad14" : "#52c41a" }}
                  icon={
                    record.approved ? (
                      <UnlockOutlined />
                    ) : (
                      <CheckSquareOutlined />
                    )
                  }
                  onClick={() =>
                    handleApprove(record.approved, record.employee_id)
                  }
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ];

    const organizationItems = _.orderBy(
      salaryColumnSettings,
      ["order"],
      ["asc"]
    ).map((item) => ({
      title: `${item.display_name} (${item.symbol.symbol})`,
      showOriginTitle: true,
      render: ({ record }: { record: EmployeeRecord }) => (
        <Text>{record.totals_by_symbol?.[item.symbol_id] ?? 0}</Text>
      ),
      sortIndex: `custom_sort${item.symbol_id}`,
      sorter: true,
      width: 135,
    }));

    return [...firstItems, ...organizationItems, ...lastItems];
  };

  const organizationSalarySymbols = salaryColumnSettings.map(
    (item) => item.symbol_id
  );

  return (
    <Surface style={{ width: window.innerWidth - 240 }}>
      {/* Header with filters and controls */}
      <Header
        onDateChange={handleDateChange}
        activeDate={activeDate}
        activeYear={activeYear}
        activeMonth={activeMonth}
        activeBranch={activeBranch}
        onTextChange={handleTextChange}
        onBranchChange={handleBranchChange}
        reload={reloadSalary}
        summary={summary}
        salary_export_type_id={salaryExportTypeId}
        organizationSalarySymbols={organizationSalarySymbols}
        salary_column_settings={salaryColumnSettings}
        onShowReport={handleShowReport}
        textFilter={filter.freeText}
      />
      <div style={{ height: "10px" }} />

      {/* Main salary data table */}
      <ActionsTable
        category="salary"
        {...({ deep: true } as any)} // Using type assertion to bypass TypeScript error
        resource="salary"
        filtersType={{
          approved: "boolean",
          exported_to_finance: "boolean",
          gender_id: "number",
        }}
        tableItems={getTableColumns()}
        current={current}
        pageSize={pageSize}
        total={total}
        order={customSort && "field" in customSort ? customSort : order}
        loading={loading}
        textFilter={filter.freeText}
        reload={reload}
        loadPage={changePage}
        items={items.map((item: EmployeeRecord, index: number) => ({
          ...item,
          id: index,
        }))}
        showheader={false}
        emptyImage={<img src={clientsImg} alt="No clients" />}
        emptytext=""
        scroll={window.innerHeight - 416}
      />

      {/* Employee salary edit/view modal */}
      <EmployeeSalaryModal
        record={selectedEmployee}
        onHide={() => toggleEmployeeSalaryModal(null)}
        visible={employeeSalaryVisible}
        mode={mode}
        onRecalculate={handleRecalculate}
        onApprove={handleApprove}
        employeeId={selectedEmployee?.employee_id}
        isApproved={selectedEmployee?.approved}
        summary={summary}
        fromSalary
      />

      {/* Report modal for salary exports */}
      <ReportModal
        visible={reportVisible}
        onHide={() => setReportVisible(false)}
      >
        {activeReport?.id && reportVisible && <Report />}
      </ReportModal>
    </Surface>
  );
};

export default Salary;
